import React, { useContext, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { DEFAULT_CANVAS_PREFERENCES } from './pedigree-utils';
import { isAnonymousOrLoading, clampLabelsSet } from './anonymousPedigrees';
import axios from 'src/axios';
import { APICONSTANTS, PEDIGREE } from '../Constants';
import { EVENTS } from '../admin/appReducer';

import Pedigree from './pedigree-tool/PedigreeChart';
import { buildConfigMap } from './pedigree-tool/pedigree/configLookup';

const Container = styled(Box)({
  width: '100%',
  position: 'relative'
});

export default function PedigreeBox({
  patient,
  isLocked,
  isMine,
  lockState,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  personLockHook
}) {
  const [canvasPreferences, setCanvasPreferences] = React.useState(
    DEFAULT_CANVAS_PREFERENCES
  );
  const { setSnack } = React.useContext(SnackContext);
  const {
    dispatchUpdate,
    content: {
      pedigreeSelectedUsers: users,
      settings: { preferences, pedigreeSetting }
    }
  } = React.useContext(AppContext);

  const fetchPedigreeSetting = () => {
    axios
      .get(APICONSTANTS.pedigreeSetting)
      .then((res) => {
        if (res.data) {
          dispatchUpdate({
            type: EVENTS.SET_SETTINGS,
            tab: 'pedigreeSetting',
            value: res.data
          });
        }
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'An error occurred while fetching values',
          severity: 'error'
        });
      });
  };

  React.useEffect(() => {
    fetchPedigreeSetting();
    localStorage.removeItem('zoom');
  }, []);

  React.useEffect(() => {
    if (preferences?.pedigreeSettings) {
      setCanvasPreferences((prev) => ({
        ...prev,
        ...preferences.pedigreeSettings
      }));
    }
  }, [preferences?.pedigreeSettings]);

  const {
    selectedUser: user,
    diagnoses = [],
    phenotypes = [],
    genotypes = []
  } = users;

  const [pedigreeConfig, setPedigreeConfig] = React.useState(null);
  // True once fetchPedigreeConfiguration has RESOLVED (success or error). The chart's
  // member mapping waits for it so a configured family never maps/lays out/paints against
  // the no-config fallback during the fetch window (spurious console warning + throwaway
  // layout computes + a visible relayout when the config landed).
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const [position, setPosition] = React.useState(new Map());
  // Phase 2: persisted within-generation order (uid -> rank). Empty until the
  // establish-order feature captures it; loaded here alongside customPositions.
  const [establishedOrder, setEstablishedOrder] = React.useState(new Map());
  // Manual-arrangement flag: the saved order is a hard LOCK only when true (user
  // drag-reordered); otherwise it just seeds orientation and layouts re-optimise.
  const [orderLocked, setOrderLocked] = React.useState(false);
  const [isMigrated, setIsMigrated] = React.useState(false);
  const [initialDateModified, setInitialDateModified] = React.useState(null);

  const fetchPedigreeConfiguration = () => {
    axios
      .get(APICONSTANTS.pedigreeConfig.replace(':user_id', user._id))
      .then((res) => {
        if (res.data.configuration) {
          const {
            pedigreeConfig,
            customPositions,
            establishedOrder: loadedOrder,
            orderLocked: loadedOrderLocked,
            migrated,
            pidMap,
            createdAt,
            updatedAt
          } = res.data.configuration;
          setInitialDateModified({ created: createdAt, updated: updatedAt });
          if (pedigreeConfig) {
            // Index every node under all the ids it carries (uid/_id/pid) so
            // the renderer resolves it regardless of whether the family is
            // born-stable (node.uid === member._id) or legacy (node.uid is a
            // UUID === member.uid). See pedigree/configLookup.js.
            const configMap = buildConfigMap(pedigreeConfig);
            setPedigreeConfig(configMap);
          }

          if (customPositions) {
            setPosition(
              new Map(Object.entries(res.data.configuration.customPositions))
            );
          }

          if (loadedOrder) {
            // values are ranks (numbers); keys are uids
            setEstablishedOrder(new Map(Object.entries(loadedOrder)));
          }
          setOrderLocked(!!loadedOrderLocked);

          setIsMigrated(!!migrated);
        }
        setConfigLoaded(true);
      })
      .catch((/*err*/) => {
        setSnack({
          status: true,
          msg: 'Error: Failed to fetch pedigree Config',
          severity: 'error'
        });
        // Resolve the gate even on error so the chart still renders via the fallback path.
        setConfigLoaded(true);
      });
  };

  React.useEffect(() => {
    // Re-arm the gate for a refetch (a user/family change without a remount).
    setConfigLoaded(false);
    fetchPedigreeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [labelsSet, setLabelsSet] = React.useState({
    ...PEDIGREE.defaultLabelFilters,
    ...PEDIGREE.legendLabelFilters,
    displayLabelMode: 'g-number',
    diseasesMap: diagnoses.reduce((acc, d) => {
      acc[d.id] = true;
      return acc;
    }, {}),
    phenotypes: true,
    phenotypesMap: phenotypes.reduce((acc, p) => {
      acc[p.id] = true;
      return acc;
    }, {}),
    genotypes: true,
    genotypesMap: genotypes.reduce((acc, g) => {
      acc[g.id] = true;
      return acc;
    }, {})
  });

  // Apply usergroup default displayLabelMode once per family-load.
  // User changes in the sidebar after this point are not clobbered.
  const defaultsAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (!pedigreeSetting) return;
    defaultsAppliedRef.current = true;
    const next = pedigreeSetting.defaultDisplayLabelMode;
    if (next && next !== 'g-number') {
      setLabelsSet((prev) => ({ ...prev, displayLabelMode: next }));
    }
  }, [pedigreeSetting]);

  React.useEffect(() => {
    setLabelsSet((prev) => {
      const mergeIntoMap = (currentMap, items) => {
        const nextMap = { ...currentMap };
        let changed = false;
        items.forEach((item) => {
          if (!(item.id in nextMap)) {
            nextMap[item.id] = true;
            changed = true;
          }
        });
        return { nextMap, changed };
      };

      const d = mergeIntoMap(prev.diseasesMap, diagnoses);
      const p = mergeIntoMap(prev.phenotypesMap, phenotypes);
      const g = mergeIntoMap(prev.genotypesMap, genotypes);

      if (!d.changed && !p.changed && !g.changed) return prev;

      return {
        ...prev,
        diseasesMap: d.nextMap,
        phenotypesMap: p.nextMap,
        genotypesMap: g.nextMap
      };
    });
  }, [diagnoses, phenotypes, genotypes]);

  // Anonymous pedigrees: clamp PII label keys at the render boundary (fail
  // closed while the settings fetch resolves), so defaults, presets and
  // persisted configurations can never draw PII node labels. The underlying
  // checkbox state is left unclamped.
  const anonymousLabels = isAnonymousOrLoading(pedigreeSetting);
  const effectiveLabelsSet = useMemo(
    () => clampLabelsSet(labelsSet, anonymousLabels),
    [labelsSet, anonymousLabels]
  );

  const options = useMemo(
    () => ({ ...canvasPreferences, labelsSet: effectiveLabelsSet }),
    [canvasPreferences, effectiveLabelsSet]
  );

  return (
    <Container>
      <Pedigree
        pedigreeConfig={pedigreeConfig}
        configLoaded={configLoaded}
        isMigrated={isMigrated}
        position={position}
        initialOrder={establishedOrder}
        initialOrderLocked={orderLocked}
        options={options}
        setOptions={setCanvasPreferences}
        labelsSet={effectiveLabelsSet}
        setLabelsSet={setLabelsSet}
        patient={patient}
        isLocked={isLocked}
        isMine={isMine}
        lockState={lockState}
        requestStatus={requestStatus}
        onRequestLock={onRequestLock}
        onCancelRequest={onCancelRequest}
        onForceAcquireLock={onForceAcquireLock}
        pendingRequest={pendingRequest}
        onRespondToRequest={onRespondToRequest}
        personLockHook={personLockHook}
        initialDateModified={initialDateModified}
      />
    </Container>
  );
}
